import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Shield,
  Award,
  Briefcase,
  DollarSign,
  Info,
  CheckCircle2,
  Edit,
  User,
  Target,
  RefreshCw,
  Calendar,
  PieChart,
  Activity,
} from "lucide-react";
import { AdvisorProfileSettings } from "./AdvisorProfileSettings";
import type { FeeStructureOption } from "../../interface/profile/FeeStructureOption";
import { CERTIFICATIONS } from "../../utils/financialTaxonomy";
import { activityLogService } from "../../services/activityLogService";

const getCertLabel = (cert: string) => {
  const trimmed = cert.trim();
  const found = CERTIFICATIONS.find(c => c.value.toUpperCase() === trimmed.toUpperCase());
  return found ? found.label : trimmed;
};

const formatFeeValue = (fee: FeeStructureOption) => {
  const parts: string[] = [];
  if (fee.aumPercentage != null) parts.push(fee.aumPercentage > 100 ? `$${fee.aumPercentage}` : `${fee.aumPercentage}%`);
  if (fee.hourlyRate != null) parts.push(`$${fee.hourlyRate}/hr`);
  if (fee.flatFeeAmount != null) parts.push(`$${fee.flatFeeAmount}`);
  if (fee.performanceFeePercentage != null) parts.push(`${fee.performanceFeePercentage}% perf`);
  if (fee.minimumFee != null) parts.push(`min $${fee.minimumFee}`);
  return parts.join(" | ");
};

interface AdvisorOverviewProps {
  advisorCertifications?: string[];
  specializations?: string[];
  yearsExperience?: string;
  assetsUnderManagement?: number;
  totalClients?: number;
  feeStructureOptions?: FeeStructureOption[];
  clientTypes?: string[];
  clientRiskTolerance?: string[];
  consultationAvailable?: boolean;
  consultationDuration?: number;
  acceptingNewClients?: boolean;
}

export function AdvisorOverview({ advisorCertifications, specializations, yearsExperience, assetsUnderManagement, totalClients, feeStructureOptions, clientTypes, clientRiskTolerance, consultationAvailable, consultationDuration, acceptingNewClients }: AdvisorOverviewProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Certifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {advisorCertifications && advisorCertifications.length > 0 ? (
                advisorCertifications.map((cert, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 border rounded-lg">
                    <Award className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-sm">{getCertLabel(cert)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No certifications listed</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Practice Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center p-3 border rounded-lg">
              <span className="text-sm font-semibold">Assets Under Management</span>
              <span>{assetsUnderManagement != null ? `$${(assetsUnderManagement / 1000000).toFixed(2)}M` : "N/A"}</span>
            </div>
            <div className="flex justify-between items-center p-3 border rounded-lg">
              <span className="text-sm font-semibold">Total Clients</span>
              <span>{totalClients != null ? totalClients : "N/A"}</span>
            </div>
            <div className="flex justify-between items-center p-3 border rounded-lg">
              <span className="text-sm font-semibold">Years of Experience</span>
              <span>{yearsExperience || "N/A"}</span>
            </div>
          </CardContent>
          {specializations && specializations.length > 0 && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                <span className="text-sm font-semibold">Specializations</span>
                <div className="flex flex-wrap gap-2">
                  {specializations.map((s, i) => (
                    <Badge key={i} variant="secondary">{s}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Fee Structure & Service Offerings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Fee Structure & Service Offerings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {feeStructureOptions && feeStructureOptions.length > 0 ? (
            <div className="space-y-3">
              {feeStructureOptions.map((fee) => (
                <div key={fee.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{fee.name}</span>
                      <Badge variant="secondary" className="text-xs">{fee.feeType.replace(/_/g, " ")}</Badge>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">{formatFeeValue(fee)}</div>
                  {fee.billingFrequency && <p className="text-xs text-muted-foreground mt-1">Billing: {fee.billingFrequency}</p>}
                  {fee.description && <p className="text-xs italic mt-1">{fee.description}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No fee structures configured</p>
          )}

          {specializations && specializations.length > 0 && (
            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium mb-2">Service Offerings</h4>
              <div className="flex flex-wrap gap-2">
                {specializations.map((s, i) => (
                  <Badge key={i} variant="outline">{s}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consultation & Client Availability */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Consultation & Availability
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg space-y-1">
              <p className="text-sm font-semibold">Free Initial Consultation</p>
              {consultationAvailable ? (
                <>
                  <Badge variant="secondary">Available</Badge>
                  {consultationDuration != null && <p className="text-sm text-muted-foreground mt-1">{consultationDuration} minute consultation</p>}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Not offered</p>
              )}
            </div>
            <div className="p-4 border rounded-lg space-y-1">
              <p className="text-sm font-semibold">New Client Availability</p>
              {acceptingNewClients ? (
                <p className="text-sm text-green-600 font-medium">Accepting New Clients</p>
              ) : (
                <p className="text-sm text-orange-600 font-medium">Not Accepting</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Types & Risk Tolerance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Client Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {clientTypes && clientTypes.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Client Types</p>
              <div className="flex flex-wrap gap-2">
                {clientTypes.map((ct, i) => (
                  <Badge key={i} variant="secondary">{ct}</Badge>
                ))}
              </div>
            </div>
          )}
          {clientRiskTolerance && clientRiskTolerance.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Preferred Client Risk Tolerance</p>
              <div className="flex flex-wrap gap-2">
                {clientRiskTolerance.map((rt, i) => (
                  <Badge key={i} variant="outline">{rt}</Badge>
                ))}
              </div>
            </div>
          )}
          {(!clientTypes || clientTypes.length === 0) && (!clientRiskTolerance || clientRiskTolerance.length === 0) && (
            <p className="text-sm text-muted-foreground">No client profile data configured</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function AdvisorActivity({ userId }: { userId?: string }) {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    activityLogService.getActivityLogsByUser(userId)
      .then(res => {
        const data = res?.data || res || [];
        setActivities(Array.isArray(data) ? data : []);
      })
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [userId]);

  const getActivityIcon = (type?: string) => {
    const t = type?.toLowerCase() || "";
    if (t.includes("rebalanc") || t.includes("trade") || t.includes("buy") || t.includes("sell"))
      return { icon: RefreshCw, bg: "bg-green-100 dark:bg-green-900", color: "text-green-600 dark:text-green-300" };
    if (t.includes("meeting") || t.includes("call") || t.includes("appointment"))
      return { icon: Calendar, bg: "bg-blue-100 dark:bg-blue-900", color: "text-blue-600 dark:text-blue-300" };
    if (t.includes("goal") || t.includes("target"))
      return { icon: Target, bg: "bg-purple-100 dark:bg-purple-900", color: "text-purple-600 dark:text-purple-300" };
    if (t.includes("deposit") || t.includes("withdrawal") || t.includes("transfer") || t.includes("payment"))
      return { icon: DollarSign, bg: "bg-orange-100 dark:bg-orange-900", color: "text-orange-600 dark:text-orange-300" };
    if (t.includes("alloc") || t.includes("portfolio"))
      return { icon: PieChart, bg: "bg-indigo-100 dark:bg-indigo-900", color: "text-indigo-600 dark:text-indigo-300" };
    return { icon: Activity, bg: "bg-gray-100 dark:bg-gray-800", color: "text-gray-600 dark:text-gray-300" };
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Client Interactions</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading activities...</p>
        ) : activities.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent activity</p>
        ) : (
          <div className="space-y-4">
            {activities.map((a: any, i: number) => {
              const { icon: Icon, bg, color } = getActivityIcon(a.activityType);
              return (
                <div key={a.id || i} className="flex items-start gap-3 pb-3 border-b last:border-b-0">
                  <div className={`p-2 rounded-full ${bg}`}>
                    <Icon className={`h-4 w-4 ${color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">{a.description || a.activityType}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(a.createdAt || a.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface AdvisorSettingsProps {
  feeStructureOptions?: FeeStructureOption[];
  specializations?: string[];
  consultationAvailable?: boolean;
  consultationDuration?: number;
  acceptingNewClients?: boolean;
}

export function AdvisorSettings({ feeStructureOptions, specializations, consultationAvailable, consultationDuration, acceptingNewClients }: AdvisorSettingsProps) {
  return (
    <div>
      <AdvisorProfileSettings feeStructureOptions={feeStructureOptions} specializations={specializations} consultationAvailable={consultationAvailable} consultationDuration={consultationDuration} acceptingNewClients={acceptingNewClients} />
    </div>
  );
}
